import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("No verification token in link."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        setMessage("Your email is verified. Welcome to BeatCredsi.");
      } catch (err) {
        setStatus("error");
        setMessage(formatApiError(err) || "Verification link is invalid or expired.");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display text-2xl font-medium mb-10" data-testid="verify-home-link">
          BeatCred<span className="text-amber-500">si</span><span className="text-amber-500 text-xs align-super">™</span>
        </Link>
        <div className="border border-white/10 bg-[#0c0c0c] p-10 text-center">
          {status === "loading" && (
            <div data-testid="verify-loading">
              <Loader2 size={32} className="text-amber-500 mx-auto animate-spin mb-4" />
              <div className="text-overline mb-2">Verifying</div>
              <p className="text-sm text-neutral-400">Confirming your email…</p>
            </div>
          )}
          {status === "success" && (
            <div data-testid="verify-success">
              <CheckCircle2 size={32} className="text-emerald-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl mb-2">Email Verified</h1>
              <p className="text-sm text-neutral-400 mb-6">{message}</p>
              <Link to="/dashboard"
                data-testid="verify-go-dashboard"
                className="inline-block px-6 py-3 bg-amber-500 text-black font-semibold text-sm hover:brightness-110 transition">
                Go to Dashboard →
              </Link>
            </div>
          )}
          {status === "error" && (
            <div data-testid="verify-error">
              <XCircle size={32} className="text-rose-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl mb-2">Verification Failed</h1>
              <p className="text-sm text-neutral-400 mb-6">{message}</p>
              <Link to="/dashboard"
                className="inline-block text-amber-500 hover:underline text-sm font-mono-tech uppercase tracking-widest">
                Return to Dashboard
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
